import React from 'react';

const INLINE_MARKDOWN_RE = /(\*\*.*?\*\*|_.*?_)/g;

// Helper to parse bold/italic markdown in a line of text, rendering HTML directly if tags are present
export const parseInlineMarkdown = (line) => {
  if (!line) return '';
  const lineStr = String(line);
  if (/<[a-z][\s\S]*>/i.test(lineStr)) {
    const html = markdownInlineToHtml(lineStr);
    return <span dangerouslySetInnerHTML={{ __html: html }} />;
  }

  // Split on **bold** and _italic_
  const parts = lineStr.split(INLINE_MARKDOWN_RE);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('_') && part.endsWith('_')) {
      return <em key={index}>{part.slice(1, -1)}</em>;
    }
    return part;
  });
};

// HTML-string counterpart of parseInlineMarkdown, for content that must flow
// through the TTS word-by-word highlighter (SlideRenderer.renderContent).
// That highlighter only knows how to preserve real tags (via its DOM walk in
// renderHtmlWithSpeech) — it has no notion of raw ** / _ markers — so plain
// text with markdown needs those markers converted to <strong>/<em> up front
// or they print literally instead of formatting.
export const markdownInlineToHtml = (text) => {
  if (!text) return '';
  const parts = text.split(INLINE_MARKDOWN_RE);
  return parts.map((part) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return `<strong>${part.slice(2, -2)}</strong>`;
    }
    if (part.startsWith('_') && part.endsWith('_')) {
      return `<em>${part.slice(1, -1)}</em>`;
    }
    return part;
  }).join('');
};

export const renderPlainTextWithFormatting = (text) => {
  if (!text) return null;

  const textStr = String(text).trim();

  // If there are no newlines, just return the parsed inline markdown directly (avoid wrapping in block elements like <p>)
  if (!textStr.includes('\n')) {
    return parseInlineMarkdown(textStr);
  }

  const lines = textStr.split('\n');
  const renderedElements = [];
  let inList = false;
  let listItems = [];

  const flushList = (key) => {
    if (listItems.length > 0) {
      renderedElements.push(
        <ul key={key} style={{ margin: '8px 0', paddingLeft: '20px', listStyleType: 'disc' }}>
          {listItems.map((item, idx) => (
            <li key={idx} style={{ margin: '4px 0' }}>{item}</li>
          ))}
        </ul>
      );
      listItems = [];
      inList = false;
    }
  };

  lines.forEach((line, idx) => {
    const trimmedLine = line.trim();
    if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('• ')) {
      inList = true;
      listItems.push(parseInlineMarkdown(trimmedLine.slice(2)));
    } else if (trimmedLine === '') {
      flushList(`list-${idx}`);
      // Add a paragraph break spacer or newline
      renderedElements.push(<div key={`spacer-${idx}`} style={{ height: '8px' }} />);
    } else {
      flushList(`list-${idx}`);
      renderedElements.push(
        <p key={`p-${idx}`} style={{ margin: '6px 0', lineHeight: '1.5' }}>
          {parseInlineMarkdown(line)}
        </p>
      );
    }
  });

  flushList('list-end');
  return <>{renderedElements}</>;
};
